import React from 'react';
import { Card, PIECE_COLORS, ROLE_COLORS, UNASSIGNED_COLOR } from './types';

export type ColorMode = 'none' | 'piece' | 'role';

type IProps = {
  card: Pick<Card, 'strokes' | 'pieces'>;
  size?: number;
  colorMode?: ColorMode;
  /** only the strokes of these pieces are inked; the rest is a faint silhouette */
  highlightPieces?: number[] | null;
  numbers?: boolean;
  grid?: boolean;
  /** animation: strokes below this value are drawn; a fraction draws part of a stroke. Undefined = all drawn. */
  progress?: number;
};

const polylinePoints = (pts: [number, number][]) => pts.map((p) => `${p[0]},${p[1]}`).join(' ');
export const pathLength = (pts: [number, number][]) => {
  let len = 0;
  for (let i = 1; i < pts.length; i += 1) {
    len += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
  }
  return len;
};

export const strokeColor = (card: Pick<Card, 'strokes' | 'pieces'>, i: number, mode: ColorMode): string => {
  const s = card.strokes[i];
  if (mode === 'none') return '#111827';
  if (s.piece === null) return UNASSIGNED_COLOR;
  const piece = card.pieces[s.piece];
  if (mode === 'role') return ROLE_COLORS[piece?.role ?? 'none'];
  return PIECE_COLORS[s.piece % PIECE_COLORS.length];
};

/**
 * Renders the strokes of a card. makemeahanzi's coordinates are 1024 units wide with the
 * y axis pointing up and the baseline at 900, hence the flip.
 */
export const HanziGlyph: React.FC<IProps> = ({
  card,
  size = 120,
  colorMode = 'piece',
  highlightPieces,
  numbers,
  grid = true,
  progress,
}) => {
  const strokes = card.strokes;
  const total = progress ?? strokes.length;
  const uid = React.useMemo(() => Math.random().toString(36).slice(2), []);
  const inked = (i: number) =>
    !highlightPieces || (strokes[i].piece !== null && highlightPieces.includes(strokes[i].piece as number));
  return (
    <svg
      viewBox="0 0 1024 1024"
      width={size}
      height={size}
      style={{ display: 'block', background: '#fff', border: '1px solid #e5e7eb' }}
    >
      {grid && (
        <g stroke="#eef2f7" strokeWidth={3} strokeDasharray="14 14">
          <line x1={0} y1={0} x2={1024} y2={1024} />
          <line x1={1024} y1={0} x2={0} y2={1024} />
          <line x1={512} y1={0} x2={512} y2={1024} />
          <line x1={0} y1={512} x2={1024} y2={512} />
        </g>
      )}
      <g transform="scale(1, -1) translate(0, -900)">
        {strokes.map((s, i) => {
          const color = inked(i) ? strokeColor(card, i, colorMode) : '#e5e7eb';
          if (total >= i + 1) {
            return <path key={i} d={s.path} fill={color} />;
          }
          if (total > i) {
            // partial stroke: the ink runs along the median, clipped by the outline
            const len = pathLength(s.median) + 10;
            return (
              <g key={i}>
                <clipPath id={`hz-${uid}-${i}`}>
                  <path d={s.path} />
                </clipPath>
                <polyline
                  points={polylinePoints(s.median)}
                  fill="none"
                  stroke={color}
                  strokeWidth={220}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  clipPath={`url(#hz-${uid}-${i})`}
                  strokeDasharray={len}
                  strokeDashoffset={len * (1 - (total - i))}
                />
              </g>
            );
          }
          return <path key={i} d={s.path} fill="#eef2f7" />;
        })}
      </g>
      {numbers &&
        strokes.map((s, i) => {
          const p = s.median[0];
          if (!p) return null;
          const x = p[0];
          const y = 900 - p[1];
          return (
            <g key={'n' + i} transform={`translate(${x},${y})`}>
              <circle
                r={34}
                fill="#fff"
                stroke={inked(i) ? strokeColor(card, i, colorMode) : '#9ca3af'}
                strokeWidth={6}
              />
              <text textAnchor="middle" dominantBaseline="central" fontSize={46} fontWeight={700} fill="#111827">
                {i + 1}
              </text>
            </g>
          );
        })}
    </svg>
  );
};

/** Small preview that fetches nothing: the character itself in a CJK font, or the id when there is none. */
export const CharBadge: React.FC<{ chr: string | null; id: string; size?: number }> = ({ chr, id, size = 28 }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: size + 8,
      height: size + 8,
      padding: '0 4px',
      border: '1px solid #e5e7eb',
      borderRadius: 4,
      background: '#fff',
      fontSize: chr ? size : 11,
      lineHeight: 1,
      fontFamily: chr ? '"Noto Sans SC", "PingFang SC", "Microsoft YaHei", sans-serif' : 'inherit',
      color: chr ? '#111827' : '#6b7280',
    }}
  >
    {chr ?? id}
  </span>
);
